import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Settings, UserSearch, Users, X, Check, ChevronDown } from "lucide-react";
import Reveal from "./Reveal";
import { useSite } from "@/context/SiteContext";

const levelIcons = { founder: User, ops: Settings, recruit: UserSearch, team_lead: Users, specialist: Users };

function MemberModal({ member, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-[#05081C]/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="team-member-modal-backdrop"
    >
      <motion.div
        initial={{ opacity: 0, y: 32, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-3xl bg-white text-[#0F172A] p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="team-member-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#F1F5F9] transition-colors" data-testid="team-member-modal-close" aria-label="Close">
          <X size={18} />
        </button>
        <img
          src={member.photo_url}
          alt={member.name}
          className="w-28 h-28 rounded-full object-cover mx-auto border-4 border-[#2FBFE8]"
        />
        <h3 className="mt-5 font-display font-extrabold text-2xl text-center">{member.name}</h3>
        <p className="text-center text-base font-bold text-[#5A6B8C] mt-1">{member.role}</p>
        <p className="mt-4 text-sm text-[#47546E] leading-relaxed text-center">{member.bio}</p>
        {member.responsibilities?.length > 0 && (
          <>
            <div className="mt-5 eyebrow text-[#0096D6] text-[9px]">Key responsibilities</div>
            <ul className="mt-2.5 space-y-2">
              {member.responsibilities.map((r) => (
                <li key={r} className="flex items-start gap-2 text-sm text-[#334155]">
                  <span className="w-[18px] h-[18px] rounded-full bg-[rgba(0,180,230,0.12)] border border-[rgba(0,180,230,0.4)] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={10} className="text-[#00A3E0]" strokeWidth={3.5} />
                  </span>
                  {r}
                </li>
              ))}
            </ul>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function GradientPhoto({ src, alt, size = 84 }) {
  return (
    <span
      className="rounded-full p-[3px] flex-shrink-0"
      style={{ background: "linear-gradient(135deg, #00CFFF 0%, #1D6FF2 100%)" }}
    >
      {src ? (
        <img src={src} alt={alt} className="rounded-full object-cover bg-[#0A1628]" style={{ width: size, height: size }} />
      ) : (
        <span className="rounded-full bg-[#0A2238] flex items-center justify-center" style={{ width: size, height: size }}>
          <User size={size * 0.4} className="text-cyan-300" />
        </span>
      )}
    </span>
  );
}

function MemberNode({ m, onOpen }) {
  return (
    <button
      onClick={() => onOpen(m)}
      className="group flex items-center gap-5 rounded-2xl bg-[rgba(9,18,36,0.62)] backdrop-blur-md border border-[rgba(0,207,255,0.45)] px-7 py-6 shadow-[0_0_26px_rgba(0,191,255,0.14),0_16px_40px_rgba(0,0,0,0.4)] transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,191,255,0.28),0_20px_50px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 sm:w-[300px]"
      data-testid={`team-node-${(m.name || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <GradientPhoto src={m.photo_url} alt={m.name} />
      <span className="text-left">
        <span className="block font-display font-extrabold text-[18px] text-white leading-tight">{m.name}</span>
        <span className="block text-[14px] font-semibold text-[#B9C6DB] mt-1">{m.role}</span>
      </span>
    </button>
  );
}

function LabelNode({ level }) {
  const Icon = levelIcons[level] || Users;
  return (
    <div className="flex items-center gap-5 rounded-2xl bg-[rgba(9,18,36,0.62)] backdrop-blur-md border border-[rgba(0,207,255,0.45)] px-7 py-6 shadow-[0_0_26px_rgba(0,191,255,0.14),0_16px_40px_rgba(0,0,0,0.4)] sm:w-[300px]">
      <GradientPhoto />
      <span className="font-display font-extrabold text-[18px] text-white">{level === "team_lead" ? "Team Leads" : level === "specialist" ? "Specialists" : ""}</span>
    </div>
  );
}

const CYAN_LINE = "bg-[#2FBFE8]";

export default function TeamSection() {
  const { content } = useSite();
  const [selected, setSelected] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const team = content.team || [];

  const byLevel = (lv) => team.filter((m) => m.level === lv);

  const renderLevel = (lv) => {
    const members = byLevel(lv);
    if (members.length === 0) return <LabelNode level={lv} />;
    return members.map((m) => <MemberNode key={m.id || m.name} m={m} onOpen={setSelected} />);
  };

  const hasMore = byLevel("team_lead").length > 0 || byLevel("specialist").length > 0;

  return (
    <section
      id="team"
      className="section-pad relative overflow-hidden text-white"
      style={{ backgroundImage: "url(/team-bg.png)", backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundImage: "url(/team-bg.png)", backgroundSize: "cover", backgroundPosition: "center", filter: "blur(22px)", transform: "scale(1.12)" }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-[rgba(6,13,28,0.74)]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <Reveal direction="left">
            <div className="eyebrow text-[#00CFFF] mb-4">Our team structure</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-[46px] tracking-tight leading-[1.12] text-white">
              Meet the team behind <span className="text-[#00CFFF]">your team.</span>
            </h2>
            <p className="mt-4 text-white/85 text-base sm:text-lg max-w-2xl leading-relaxed">
              From recruitment to day-to-day support, our team works together to make sure you get the right people and a smooth experience.
            </p>
          </Reveal>
          <Reveal direction="right" className="lg:pt-10">
            <button onClick={() => setShowAll((s) => !s)} className="btn btn-outline-light" data-testid="explore-team-button">
              Explore Our Team
              <ChevronDown size={17} strokeWidth={2.5} className={`transition-transform duration-300 ${showAll ? "rotate-180" : ""}`} />
            </button>
          </Reveal>
        </div>

        <Reveal direction="up" delay={0.1}>
          <div className="flex flex-col items-center mt-14" data-testid="team-org-chart">
            {renderLevel("founder")}
            {/* drop + split connector */}
            <div className={`w-[2px] h-8 ${CYAN_LINE}`} />
            <div className={`hidden sm:block relative w-[380px] h-[2px] ${CYAN_LINE}`}>
              <span className={`absolute left-0 top-0 w-[2px] h-8 ${CYAN_LINE}`} style={{ transform: "translateX(-50%)" }} />
              <span className={`absolute right-0 top-0 w-[2px] h-8 ${CYAN_LINE}`} style={{ transform: "translateX(50%)" }} />
            </div>
            <div className={`sm:hidden w-[2px] h-8 ${CYAN_LINE}`} />
            <div className="flex flex-col sm:flex-row gap-6 sm:gap-20 items-center">
              {renderLevel("ops")}
              {renderLevel("recruit")}
            </div>

            <AnimatePresence initial={false}>
              {showAll && hasMore && (
                <motion.div
                  key="full-team"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden flex flex-col items-center"
                  data-testid="team-full-chart"
                >
                  <div className={`w-[2px] h-8 ${CYAN_LINE}`} />
                  <div className="flex flex-wrap justify-center gap-6">
                    {byLevel("team_lead").length > 0 ? renderLevel("team_lead") : <LabelNode level="team_lead" />}
                  </div>
                  <div className={`w-[2px] h-8 ${CYAN_LINE}`} />
                  <div className="flex flex-wrap justify-center gap-6">
                    {byLevel("specialist").length > 0 ? renderLevel("specialist") : <LabelNode level="specialist" />}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {selected && <MemberModal member={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
